import React, { useEffect, useMemo, useState } from 'react'
import { api } from 'core/api/ConnectionManager'
import helpers, { treeViewHelpers } from 'core/helpers'
import { isExist, isNotEmptyArray } from 'core/utils/coreUtil'
import { MapCreator } from 'features/map/controllers/MapCreator'
import helpersDigitalSignages from 'core/helpers/_digitalSignages'
import { licenseType, noEmoji, rateTypeRename } from 'core/utils/displayUtil'
import { ILicensesData } from '../../../core/models/DigitalSignage'
import { IGroup } from '../../iptv/__groups/Groups'
import { INotification } from '../../notificationCenter/NotificationСenterHooks'
import { IMonitorSettings } from '../../../blocks.app/deviceSettings/__monitorsSettings/MonitorSettings'
import { MapLocation } from 'features/map/models/map'
import { debounce } from 'lodash'

export interface DisplayTimezoneOption {
    id: string
    name: string
    timezoneAbbr: string
    gmtOffset: number
}

export interface DisplayTagOption {
    id?: string | number
    name: string
    label: string
}

export interface DisplayLicenseOption {
    id: string | number | null
    name: string
    vacantCount?: number
    devicesCount?: number
    expiredAt?: string | null
    paidQ?: boolean
    activeQ?: boolean
}

export interface IDisplaysAddProps {
    groups: { id: number; timezone: string }[]
    user: number
    licensesData?: {
        activeQ: boolean
        startedQ: boolean
        deletedQ: boolean
        notExpiredQ: boolean
        devicesCount: number
        licenseId: number
        companyId: number
        paidQ: boolean
        startedAt: string
        expiredAt: null | string
        manager: string
        comment: string
        createdAt: string
        deletedAt: null | string
        vacantCount: number
        licenseType: string
        rateType: null | string
        name: string
    }[]
    currentLicense?: {
        activeQ: boolean
        expiredAt: null | string
        id: number
        name: string
        paidQ: boolean
        vacantCount: number
    } | null
    activeQ?: boolean
    data: {
        settings: { monitorSettings: IMonitorSettings }
        department?: string
        activeQ: boolean | null
        groupScheduleMode: string
        orientation: string | null
        timezone: string
        name: string
        description: string
        overwriteSettings: boolean
        parentId: number
        lat: number
        lng: number
        id: number
        address: string
        tags: DisplayTagOption[]
        groupId: number
        licenseId?: number | null
        DSNotificationsData?: { unitId: number | null; floorId: number | null } | null
        platform: string
    }
    setPartialData: (data: any) => void
    address: string
    lng: number
    lat: number
    isEdit: boolean
    setGroups: (value: string[]) => void
    closeModal: () => void
    clearData: () => void
    emitError: (error: string) => void
    timezones: DisplayTimezoneOption[]
    tagsList: DisplayTagOption[]
    licenseId?: number | null
}

export interface IState {
    validateSymbolNumber: boolean
    active: boolean
    initGroup: number
    licensesData: DisplayLicenseOption[]
    currentLicense: DisplayLicenseOption | null
    mapCenter: { lat: number; lng: number } | null
    initialAddress: string | null
    groupTimezone: boolean
    parentTimezone: string | null
    addressSuggestions: MapLocation[]
    data?: {
        nullTimezoneError?: string | null
        error?: string | null
    }
    unitList: INotification[]
}

const mapFactory = MapCreator.getInstance().getMapFactory()
const mapApi = mapFactory.createMapApi()

const updateMapCenter = (state: any, lat: number, lng: number) => {
    if (isExist(lat) && isExist(lng)) {
        return {
            ...state,
            mapCenter: { lat, lng },
        }
    }

    return {
        ...state,
        mapCenter: { lat: null, lng: null },
    }
}

const updateInitialAddress = (state: any, address: string) => {
    return {
        ...state,
        initialAddress: address,
    }
}

export const DisplaysAddMethods = (p_: IDisplaysAddProps) => {
    const [state, setState] = useState<IState>({
        active: false,
        initGroup: p_.data.groupId,
        mapCenter: null,
        initialAddress: null,
        groupTimezone: false,
        parentTimezone: null,
        currentLicense: null,
        licensesData: [],
        addressSuggestions: [],
        data: {
            nullTimezoneError: null,
            error: null,
        },
        validateSymbolNumber: false,
        unitList: [],
    })

    useEffect(() => {
        const methodName = getMethodName()
        const methodData = getMethodData()
        const { setGroups, licenseId } = p_
        getLicenses(p_.user)
        api.send(methodName, { nestedQ: false, ...methodData }, { hideLoader: true }).then((availableFolders: any) => {
            const groups = treeViewHelpers.addParentKeyIdForTree(availableFolders, 'parentId')
            setGroups(groups)
        })
        if (licenseId) {
            p_.setPartialData({ licenseId })
        }

        api.send<object, Notification[]>('getDsNotifications', {}).then((res) => {
            if (!res) return

            setState((prevState: any) => ({
                ...prevState,
                unitList: res,
            }))
        })
    }, [])

    useEffect(() => {
        if (state.groupTimezone && p_.data && p_.data.parentId) {
            const parentTimezone: any = !isGroupsEmpty() && p_.groups.find((group) => group.id === p_.data.parentId)
            if (parentTimezone && parentTimezone['0'] && parentTimezone['1'] && parentTimezone['2']) {
                setState((prevState) => {
                    return {
                        ...prevState,
                        groupTimezone: true,
                        parentTimezone: parentTimezone['0'].timezone,
                        data: { ...prevState.data, nullTimezoneError: null },
                    }
                })

                p_.setPartialData({
                    timezone: parentTimezone['0'].timezone,
                    timezoneOffset: parentTimezone['1'].timezoneOffset,
                    timezoneAbbr: parentTimezone['2'].timezoneAbbr,
                })
            }
        }
    }, [state.groupTimezone])

    const isGroupsEmpty = () => {
        return p_.groups.length === 0
    }

    const getMethodName = () => {
        const { isEdit } = p_
        return isEdit ? 'getAvailableParentGroups' : 'getAvailableToCreateParentGroups'
    }

    const getMethodData = () => {
        const { isEdit } = p_
        const { id } = p_.data

        return isEdit ? { digitalSignageId: id } : {}
    }

    const getLicenses = (companyId: number) => {
        api.send<object, { data: ILicensesData }>('getLicenseByFilters', {
            sort: [['licenseId', 'ASC']],
            limit: 1000,
            companyId: companyId,
        }).then((res) => {
            let optionFormat = res.data
                .filter((data) => data.activeQ)
                .map((opt) => ({
                    id: opt.licenseId,
                    name: rateTypeRename(opt.rateType || '') + ' (' + licenseType(opt) + ')',
                    vacantCount: opt.vacantCount,
                    devicesCount: opt.devicesCount,
                    expiredAt: opt.expiredAt,
                    paidQ: opt.paidQ,
                    activeQ: opt.activeQ,
                }))
            setState((prevState: any) => ({
                ...prevState,
                licensesData: optionFormat,
            }))
        })
    }

    const getGroupsWithReplacedName = (groups: IGroup[]) => {
        let updatedGroups: IGroup[] = []

        if (groups) {
            updatedGroups = groups.map((group) => {
                return {
                    ...group,
                    name: helpers.getTranslatedIfMain(group.name || ''),
                }
            })
        }

        return updatedGroups
    }

    const clearAddressSuggestions = () => {
        setState((prevState) => ({
            ...prevState,
            addressSuggestions: [],
        }))
    }

    const fetchOpenStreetMapSuggestions = (address: string): Promise<MapLocation[]> => {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=5`

        return fetch(url)
            .then((response) => response.json())
            .then((results) => {
                if (!Array.isArray(results)) {
                    return []
                }

                return results.map((item: { place_id?: number; display_name: string; lat: string; lon: string }) => ({
                    id: item.place_id,
                    name: item.display_name,
                    description: '',
                    lat: parseFloat(item.lat),
                    lng: parseFloat(item.lon),
                }))
            })
    }

    const loadAddressSuggestions = useMemo(
        () =>
            debounce((address: string) => {
                const trimmedAddress = address.trim()

                if (trimmedAddress.length < 3) {
                    clearAddressSuggestions()
                    return
                }

                const mapType = window?.spconfig?.map?.type
                const useOpenStreetMap = mapType !== 'yandex' && mapType !== 'google'

                const request = useOpenStreetMap
                    ? fetchOpenStreetMapSuggestions(trimmedAddress)
                    : mapApi.geocodeLocation(trimmedAddress)

                request
                    .then((mapLocationList) => {
                        setState((prevState) => ({
                            ...prevState,
                            addressSuggestions: mapLocationList || [],
                        }))
                    })
                    .catch(() => {
                        clearAddressSuggestions()
                    })
            }, 400),
        []
    )

    useEffect(() => {
        return () => {
            loadAddressSuggestions.cancel()
        }
    }, [loadAddressSuggestions])

    const onChangeAddress = (address: string) => {
        const { setPartialData } = p_
        address = noEmoji(address)
        setPartialData({ address })
        loadAddressSuggestions(address)
    }

    const onSelectAddressSuggestion = (location: MapLocation) => {
        const { setPartialData } = p_
        const fullName = location.description
            ? `${location.name}, ${location.description}`.replace(/,\s*$/, '').trim()
            : location.name

        setPartialData({
            address: fullName,
            lat: location.lat,
            lng: location.lng,
        })

        setState((prevState) => ({
            ...prevState,
            initialAddress: fullName,
            addressSuggestions: [],
            mapCenter:
                typeof location.lat === 'number' && typeof location.lng === 'number'
                    ? { lat: location.lat, lng: location.lng }
                    : prevState.mapCenter,
        }))
    }

    const onChangeLocation = (location: { name: string; lat: number; lng: number }) => {
        const { setPartialData } = p_
        const { name, lat, lng } = location

        setPartialData({ address: name, lat, lng })

        setState((prevState) => {
            return {
                ...prevState,
                initialAddress: name,
                addressSuggestions: [],
            }
        })
    }

    const onChangePlacemarkGeometry = (geometry: { lat: number; lng: number }) => {
        const { setPartialData } = p_
        const { lat, lng } = geometry

        setPartialData({ lat, lng })
    }

    const onMapClose = () => {
        const { lat, lng } = p_.data

        setState((prevState: IState) => ({
            ...prevState,
            active: false,
            mapCenter: {
                lat,
                lng,
            },
        }))
    }

    const onMapOpen = () => {
        const { lat, lng } = p_.data

        setState((prevState: IState) => ({
            ...prevState,
            active: true,
            addressSuggestions: [],
            mapCenter: isExist(lat) && isExist(lng) ? { lat, lng } : prevState.mapCenter,
        }))
    }

    const updateLocation = () => {
        const { setPartialData } = p_
        const { address, lat, lng } = p_.data
        const { initialAddress } = state
        const hasCoords =
            typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)

        if ((!address || address === initialAddress) && hasCoords) {
            save()
            return
        }

        if (!address || !String(address).trim()) {
            save()
            return
        }

        const mapType = window?.spconfig?.map?.type
        const useOpenStreetMap = mapType !== 'yandex' && mapType !== 'google'
        const request = useOpenStreetMap ? fetchOpenStreetMapSuggestions(address) : mapApi.geocodeLocation(address)

        request
            .then((mapLocationList) => {
                if (!isNotEmptyArray(mapLocationList)) {
                    save()
                    return
                }

                const { lat: nextLat, lng: nextLng } = mapLocationList[0]

                setPartialData({ lat: nextLat, lng: nextLng })
                save({ lat: nextLat, lng: nextLng })
            })
            .catch(() => {
                save()
            })
    }

    const onSave = () => {
        updateLocation()
    }

    const getAddress = () => {
        const { address } = p_.data

        return address || ''
    }

    const getCoordinate = () => {
        const { lat, lng } = p_.data

        return {
            lat: lat || null,
            lng: lng || null,
        }
    }

    const getLocation = () => {
        return {
            name: getAddress(),
            ...getCoordinate(),
        }
    }

    const getCenter = () => {
        const { mapCenter } = state

        // @ts-ignore
        if (isExist(mapCenter) && isExist(mapCenter.lat) && isExist(mapCenter.lng)) {
            return mapCenter
        }

        const { lat, lng } = p_.data

        if (isExist(lat) && isExist(lng)) {
            return { lat, lng }
        }

        return null
    }

    const checkErrors = (displayInfo: { platform: string; timezone: string; name: string }) => {
        let error = null
        const platformName: string | undefined = helpersDigitalSignages.getPlatformName(displayInfo.platform)
        const availablePlatforms = ['sssp', 'tizen']

        if (platformName && availablePlatforms.includes(platformName) && !displayInfo.timezone) {
            error = 'displaysAddError'
        }

        if (!displayInfo.name) {
            error = 'nameFieldIsRequired'
        }

        return error
    }

    const save = (overrides?: { lat?: number | null; lng?: number | null }) => {
        const displayInfo: any = { ...p_.data, ...overrides }
        const error = checkErrors(displayInfo)
        const { emitError, closeModal, clearData } = p_

        if (error) {
            emitError(error)
            return
        }

        if (!p_.data.name && !p_.data.timezone) {
            setState((prevState: IState) => ({
                ...prevState,
                data: { ...prevState.data, nullTimezoneError: 'fieldIsRequired', error: 'fieldIsRequired' },
            }))
            return
        } else if (!p_.data.name) {
            setState((prevState: IState) => ({
                ...prevState,
                data: { ...prevState.data, nullTimezoneError: null, error: 'fieldIsRequired' },
            }))
            return
        } else if (!p_.data.timezone) {
            setState((prevState: IState) => ({
                ...prevState,
                data: { ...prevState.data, nullTimezoneError: 'fieldIsRequired', error: null },
            }))
            return
        }

        if (displayInfo.tags) {
            displayInfo.tags = displayInfo.tags.map((tag: { label: string }) => tag.label)
        }
        if (
            (displayInfo.DSNotificationsData && !displayInfo.DSNotificationsData.unitId) ||
            (displayInfo.DSNotificationsData && !displayInfo.DSNotificationsData.floorId)
        ) {
            displayInfo.DSNotificationsData = null
        }

        api.send('editDisplay', displayInfo).then((res) => {
            clearData()
            closeModal()
        })
    }

    const inputNullCheck = (value: string | number | null, type: string) => {
        if (type === 'name') {
            value && !/^\s*$/.test(String(value))
                ? setState((prevState: IState) => ({ ...prevState, data: { ...prevState.data, error: null } }))
                : setState((prevState: IState) => ({
                      ...prevState,
                      data: { ...prevState.data, error: 'fieldIsRequired' },
                  }))
        }
        if (type === 'timezone') {
            value
                ? setState((prevState: IState) => ({
                      ...prevState,
                      data: { ...prevState.data, nullTimezoneError: null },
                  }))
                : setState((prevState: IState) => ({
                      ...prevState,
                      data: { ...prevState.data, nullTimezoneError: 'fieldIsRequired' },
                  }))
        }
    }

    const onClose = () => {
        const { closeModal, clearData } = p_

        closeModal()
        clearData()
    }

    const onValidateSymbolNumber = (isValidate: boolean) => {
        setState((prevState: IState) => ({ ...prevState, validateSymbolNumber: isValidate }))
    }

    return {
        state,
        setState,
        onMapClose,
        getLocation,
        onChangeLocation,
        onChangePlacemarkGeometry,
        getCenter,
        onChangeAddress,
        onSelectAddressSuggestion,
        clearAddressSuggestions,
        getGroupsWithReplacedName,
        save,
        inputNullCheck,
        onMapOpen,
        getAddress,
        onValidateSymbolNumber,
        onSave,
        onClose,
    }
}
